import { inject, injectable } from 'inversify';
import { IAuthConfiguration } from './IAuthConfiguration';
import { Headers } from './auto-generated/Headers';
import { map } from 'rxjs/operators';
import HttpResponse from './auto-generated/HttpResponse';
import { Observable, from } from 'rxjs';

interface IAuthTokenResponse {
	access_token: string;
	expires_in: number;
	token_type: string;
}

const ONE_SECOND_IN_MS = 1000;

export const TOKEN_LIFESPAN_REFRESH_THRESHOLD = 0.9;
export const TOKEN_LIFESPAN_REFRESH_INTERVAL = TOKEN_LIFESPAN_REFRESH_THRESHOLD + 0.01;

@injectable()
export class TokenApi {
	private basePath: string = 'https://localapi.sageworks.com';
	private token: IAuthTokenResponse | null = null;
	private clientId: string;
	private clientSecret: string;
	private grantType: string;
	private subdomain: string | null = null;
	private lastTokenRetrievalTime: number | null = null;
	// CookieName is a LocalStorage key name. This will be used to access a stashed GrantTypeToken from LocalStorage.
	// GrantTypeTokens are specific by CustomersID and environment. If your app will require multiple sessions
	// differing by CustomersIDs and/or environment, CookieName must be unique for each case.
	private cookieName: string;

	constructor(@inject('IAuthConfiguration') private AuthConfiguration: IAuthConfiguration) {
		if (this.AuthConfiguration.basePath) {
			this.basePath = this.AuthConfiguration.basePath;
		}
		if (this.AuthConfiguration.subdomain) {
			this.subdomain = this.AuthConfiguration.subdomain;
		}
		this.clientId = this.AuthConfiguration.clientId;
		this.clientSecret = this.AuthConfiguration.clientSecret;
		this.grantType = this.AuthConfiguration.grantType;
		this.cookieName = this.AuthConfiguration.cookieName;
	}

	set(token: IAuthTokenResponse) {
		this.lastTokenRetrievalTime = Date.now();
		this.token = token;
	}

	validateToken() {
		if (!this.token || !this.lastTokenRetrievalTime) return false;
		if (Date.now() - this.lastTokenRetrievalTime > this.token.expires_in * ONE_SECOND_IN_MS * TOKEN_LIFESPAN_REFRESH_THRESHOLD) return false;
		return true;
	}

	get(): Promise<IAuthTokenResponse> {
		if (this.validateToken() && this.token) {
			return Promise.resolve(this.token);
		} else {
			const promise = this.refreshToken();
			promise.then(token => {
				this.set(token);
			});
			return promise;
		}
	}

	private refreshToken(): Promise<IAuthTokenResponse> {
		const clientId = this.clientId;
		const clientSecret = this.clientSecret;
		const grantType = this.grantType;
		const customer_token = localStorage.getItem(this.cookieName);
		let data = 'client_id=' + clientId + '&client_secret=' + clientSecret + '&grant_type=' + grantType + '&' + grantType + '=' + customer_token;
		if (this.subdomain) {
			data += '&subdomain=' + this.subdomain;
		}
		const headers = {} as Headers;
		headers['Content-Type'] = 'application/x-www-form-urlencoded';
		return this.performNetworkCall(`${this.basePath}/connect/token`, 'post', data, headers)
			.pipe(
				map(
					(result: HttpResponse): IAuthTokenResponse => {
						if (result.response as IAuthTokenResponse) {
							return result.response;
						}
						return {} as IAuthTokenResponse;
					}
				)
			)
			.toPromise();
	}

	private performNetworkCall(url: string, method: string, body?: any, headers?: Headers): Observable<HttpResponse> {
		let promise = window
			.fetch(url, {
				method: method,
				body: body,
				headers: <any>headers
			})
			.then(response => {
				let headers: Headers = {};
				response.headers.forEach((value, name) => {
					headers[name.toString().toLowerCase()] = value;
				});
				return response.text().then(text => {
					let contentType = headers['content-type'] || '';
					let payload = contentType.match('application/json') ? JSON.parse(text) : text;
					let httpResponse = new HttpResponse(payload, response.status, headers);

					if (response.status >= 400) throw httpResponse;
					return httpResponse;
				});
			});

		return from(promise);
	}

	async getTokenWithAuth0Token(auth0Token?: string): Promise<IAuthTokenResponse> {
		const clientId = this.clientId;
		const clientSecret = this.clientSecret;
		const grantType = 'online_portal_auth_zero';
		const token = auth0Token;
		let data = 'client_id=' + clientId + '&client_secret=' + clientSecret + '&grant_type=' + grantType + '&token' + '=' + token;
		if (this.subdomain) {
			data += '&subdomain=' + this.subdomain;
		}
		const headers = {} as Headers;
		headers['Content-Type'] = 'application/x-www-form-urlencoded';
		return this.performNetworkCall(`${this.basePath}/connect/token`, 'post', data, headers)
			.pipe(
				map(
					(result: HttpResponse): IAuthTokenResponse => {
						if (result.response as IAuthTokenResponse) {
							return result.response;
						}
						return {} as IAuthTokenResponse;
					}
				)
			)
			.toPromise();
	}
}